import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject as RxjsSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LangUpdateService } from '../../../../_services/lang-update.service';
import { DataSyncProgress, ProgressBarDescription } from '../../../../_services/offline-data-sync.service';
import { DATA_SYNC_STEP } from '../../../electron.constants';
import { ProgressDialogService } from './progress-dialog.service';

@Component({
    selector: 'app-progress-dialog',
    templateUrl: './progress-dialog.component.html',
    styleUrls: ['./progress-dialog.component.scss'],
    standalone: false
})
export class ProgressDialogComponent implements OnDestroy {

  dialogTitle: string;
  dialogDescription: string;
  DescriptionList: ProgressBarDescription[];
  onDestroy$ = new RxjsSubject();
  roundedSyncProgress: number;

  isDisabledCancel = false;
  isCompleted = false;
  isError = false;
  browserFlag = true;

  /** Number of data to be synchronized */
  totalDataCount: number;
  /** Number of synchronized data */
  successCount: number;
  /** Synchronization Status（%） */
  syncProgress: number;

  /**
   * data sync buffer progress（％）
   */
  syncStepProgress: number;

  /** Number of synchronized step */
  successSyncStep: number;

  /**
   * DATA_SYNC_STEP
   */
  syncStepCount: number;

  constructor(
    public dialogRef: MatDialogRef<ProgressDialogComponent>,
    private progressDialogService: ProgressDialogService,
    @Inject(MAT_DIALOG_DATA) data: { syncStepCount: number },
    private translate: TranslateService,
    private langService: LangUpdateService
  ) {
    this.totalDataCount = 0;
    this.syncStepProgress = 0;
    this.roundedSyncProgress = 0;
    this.syncStepCount = data.syncStepCount;
    this.successSyncStep = 0;
    this.successCount = 0;
    this.syncProgress = 0;
    this.dialogTitle = '';
    this.setTotalDataCount();
    this.setSyncProgress();
    this.langService.prepare(translate, this.onDestroy$);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
  }

  closeModal() {
    this.dialogRef.close();
  }

  setTotalDataCount(): void {
    // Calculate the total amount of data to be synchronized.
    this.progressDialogService.totalDataCount$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (dataSyncProgress: DataSyncProgress) => {
          this.syncStepProgress += 1 / this.syncStepCount * 100;
          this.successSyncStep += 1;

          this.DescriptionList = this.setDescriptionList(dataSyncProgress.descriptionList);

          switch (dataSyncProgress.syncStep) {
            case DATA_SYNC_STEP.UPLOAD_INSPECTION:
              this.setDisplayValue('syncProgressBarDialog.title.upload', 'syncProgressBarDialog.description.uploadInspection');
              this.dialogDescription = `${dataSyncProgress.dataTitle}${this.dialogDescription}`;
              break;
            case DATA_SYNC_STEP.DOWNLOAD_OFFLINE_SETTINGS:
              this.setDisplayValue('syncProgressBarDialog.title.download', 'syncProgressBarDialog.description.downloadOfflineSettings');
              break;
            case DATA_SYNC_STEP.DOWNLOAD_IMAGE_FILE:
              this.setDisplayValue('syncProgressBarDialog.title.download', 'syncProgressBarDialog.description.downloadOfflineSettingsImageFile');
              break;
            case DATA_SYNC_STEP.DOWNLOAD_CHECKED_OUT_INSPECTIONS:
              this.setDisplayValue('syncProgressBarDialog.title.download', 'syncProgressBarDialog.description.downloadCheckedOutInspections');
              break;
          }

          // The total number of data and the count of processed are initialized for each DATA_SYNC_STEP.
          this.totalDataCount = dataSyncProgress.totalDataCount;
          this.successCount = 0;
        },
        complete: () => {
          this.isCompleted = true;
          this.closeModal();
        },
        error: () => {
          this.isError = true;
        }
    });
  }

  setSyncProgress(): void {
    this.progressDialogService.uploadedProgressStep$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((syncStep: DATA_SYNC_STEP) => {
        this.successCount += 1;
        this.DescriptionList.map(value => {
          if (value.syncType === syncStep) {
            value.successCount += 1;
          }
        });

        this.syncProgress += (1 / this.totalDataCount * 100) / this.syncStepCount;
        this.roundedSyncProgress = Math.floor(this.syncProgress);
      });
  }

  /**
   * @param dialogTitleKey translate json key of progress dialog title
   * @param dialogDescriptionKey translate json ket of progress dialog description
   */
  setDisplayValue(dialogTitleKey: string, dialogDescriptionKey: string): void {
    this.dialogTitle = this.translate.instant(dialogTitleKey);
    this.dialogDescription = this.translate.instant(dialogDescriptionKey);
  }

  setDescriptionList(descriptionList: ProgressBarDescription[]): ProgressBarDescription[] {
    descriptionList.map(value => {
      value.successCount = 0;
      switch (value.syncType) {
        case DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE:
          value.description = this.translate.instant('syncProgressBarDialog.description.uploadInspectionImageFile');
          break;
        case DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_INFO:
          value.description = this.translate.instant('syncProgressBarDialog.description.uploadInspectionImageInfo');
          break;
        case DATA_SYNC_STEP.UPLOAD_REPORT_DATA:
          value.description = this.translate.instant('syncProgressBarDialog.description.uploadReport');
          break;
        case DATA_SYNC_STEP.DOWNLOAD_REPORT_TEMPLATE_IMAGE_FILE:
          value.description = this.translate.instant('syncProgressBarDialog.description.downloadReportTemplateImageFile');
          break;
        case DATA_SYNC_STEP.DOWNLOAD_SUBJECT_MODEL_IMAGE_FILE:
          value.description = this.translate.instant('syncProgressBarDialog.description.downloadSubjectModelImageFile');
          break;
        case DATA_SYNC_STEP.DOWNLOAD_CHECKED_OUT_INSPECTIONS:
          value.description = this.translate.instant('syncProgressBarDialog.description.downloadInspectionFiles');
          break;
      }
    });
    return descriptionList;
  }

}
